import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Table, Tooltip } from 'antd';
import { ExclamationTriangleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  SectionCard,
  StatusBadge,
  ToggleSwitch,
  TWButton,
  TWDataTable,
  TWDateRangePicker,
  TWInput,
  TWSelect,
} from '../../components/ui';
import { formatCurrency } from '../../utils/baseUtils';
import { TableRowsSkeleton } from '../../components/skeleton/skeleton';

const DASH = '—';

// EnumHelper.ConnectStatusEnum, the same codes connectApi.js's own
// (module-private) STATUS_BY_CODE reads off GET /Connect/Status —
// platformApi.js deliberately leaves `connectStatus` as the raw numeric
// code rather than pre-labelling it, so the label lives here instead.
const CONNECT_STATUS_LABEL = {
  0: 'Not started',
  10: 'Onboarding',
  20: 'Pending',
  30: 'Restricted',
  40: 'Ready',
};

// `pending`: born for Connect, not live until Activate (spec 2026-09-25 §4.4).
const MODEL_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'legacy', label: 'Legacy' },
  { value: 'connect', label: 'Connect' },
  { value: 'pending', label: 'Pending' },
];
const MODEL_LABEL = { legacy: 'Legacy', connect: 'Connect', pending: 'Pending' };
const MODEL_TONE = { legacy: 'slate', connect: 'sky', pending: 'amber' };

// The four filter keys, kept in the URL exactly like connect-finance.js's
// own TAB_PARAM — a refresh keeps them and an admin can paste a link to a
// specific filtered view. `from`/`to` are the only ones platform-finance.js
// also reads (to build its Finance query); the other three are pure
// client-side filters over rows already on hand, and never leave this file.
const FROM_PARAM = 'from';
const TO_PARAM = 'to';
const MODEL_PARAM = 'model';
const ISSUES_PARAM = 'issues';
const SEARCH_PARAM = 'q';

// No explicit range in the URL means "this month so far" — spec §5's
// default — computed fresh on every render rather than written back,
// mirroring connect-finance.js's tab fallback comment.
const defaultFrom = () => dayjs().startOf('month');
const defaultTo = () => dayjs();

// Tabular figures only for the actual digits, same convention as
// connect-finance-tables.js's `money`. A dash is not a number and reads
// fine with the surrounding proportional text.
const money = (value) =>
  typeof value === 'number' ? <span className="tabular-nums">{formatCurrency(value)}</span> : DASH;

const count = (value) =>
  typeof value === 'number' ? <span className="tabular-nums">{value}</span> : DASH;

// Rule 2: a row carrying `error` is still shown, with an error marker in
// place of its money cells — hiding it would corrupt the totals silently.
const ErrorCell = ({ message }) => (
  <Tooltip title={message || "Couldn't be calculated"}>
    <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400">
      <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden />
      <span className="text-xs font-medium">Error</span>
    </span>
  </Tooltip>
);

// Nulls always sort as the smallest value, never as the largest — antd 5.15.3
// negates the comparator's return value for 'descend' (see
// useSorter.js's `compareFn`), so an unknown value that always compares as
// smaller ends up first on 'ascend' and last on 'descend', the same place a
// real value of negative infinity would land.
const compareNullableNumber = (dataIndex) => (a, b) => {
  const av = a[dataIndex];
  const bv = b[dataIndex];
  if (av === bv) return 0;
  if (av === null || av === undefined) return -1;
  if (bv === null || bv === undefined) return 1;
  return av - bv;
};

// Rule 1: `stripeFee === null` (webhook hasn't landed) and `failedCount ===
// null` (legacy has no failure concept) both dash via `money`/`count`'s own
// `typeof value === 'number'` check — never a fabricated $0.00 or 0. Rule 2:
// when the row carries an `error`, every money cell here swaps to the error
// marker instead of a number that would otherwise look trustworthy.
const moneyColumn = (title, dataIndex, width = 130) => ({
  title,
  dataIndex,
  key: dataIndex,
  width,
  align: 'right',
  sorter: compareNullableNumber(dataIndex),
  render: (value, row) => (row.error ? <ErrorCell message={row.error} /> : money(value)),
});

// Same error guard as moneyColumn, and for the same reason: the backend's
// ErrorRow (PlatformFinanceService.cs's AppendLegacyRowsAsync catch block)
// sets only companyid/companyname/model/error and leaves every other field
// at its C# default — paymentcount is a non-nullable int, so an uncomputable
// row still ships paymentcount: 0. Without this branch that renders as a
// real-looking "0" for a row the system explicitly could not compute, the
// exact zero-versus-unknown confusion the dash rule exists to prevent.
const countColumn = (title, dataIndex, width = 110) => ({
  title,
  dataIndex,
  key: dataIndex,
  width,
  align: 'right',
  sorter: compareNullableNumber(dataIndex),
  render: (value, row) => (row.error ? <ErrorCell message={row.error} /> : count(value)),
});

// Structural, not dependent on the fee amount being known yet: which side
// pays Stripe follows from the payment model itself
// (`stripeFeePaidByRestaurant`), so the badge renders even while the fee
// cell next to it is still a dash.
const StripeFeeCell = ({ row }) => {
  if (row.error) return <ErrorCell message={row.error} />;
  // A row with no payments this period (the $0 rows every company gets
  // since 2026-09-24) paid no fee, so "who paid it" is not a thing to say.
  if (!row.paymentCount) return <span className="inline-flex justify-end">{money(row.stripeFee)}</span>;
  return (
    <span className="inline-flex items-center justify-end gap-2">
      {money(row.stripeFee)}
      <StatusBadge
        tone={row.stripeFeePaidByRestaurant ? 'sky' : 'slate'}
        label={row.stripeFeePaidByRestaurant ? 'Restaurant paid' : 'We paid'}
        size="sm"
      />
    </span>
  );
};

// Four flags collapsed into one chip — spec §5 col. 3: four separate
// columns would make the table unreadable. `chargesEnabled`/`payoutsEnabled`
// false is the most severe (rose); `feePayerVerified` false alone is a
// softer amber; a fully healthy READY account is emerald; anything else
// (still onboarding/pending) is neutral slate. The failing checks, if any,
// are named in the tooltip rather than in the chip itself.
const connectStatusTone = (row) => {
  if (row.chargesEnabled === false || row.payoutsEnabled === false) return 'rose';
  if (row.feePayerVerified === false) return 'amber';
  if (row.connectStatus === 40) return 'emerald';
  return 'slate';
};

const connectStatusIssues = (row) => {
  const issues = [];
  if (row.chargesEnabled === false) issues.push('Restricted account');
  if (row.payoutsEnabled === false) issues.push('Payouts disabled');
  if (row.feePayerVerified === false) issues.push('Fee payer not verified');
  return issues;
};

// Legacy rows carry no Connect flags at all (platformApi.js's boolOrNull /
// numberOrNull leave them null) — a dash, not an empty/false-looking chip.
const ConnectStatusCell = ({ row }) => {
  if (row.model !== 'connect' || row.connectStatus === null || row.connectStatus === undefined) {
    return DASH;
  }
  const label = CONNECT_STATUS_LABEL[row.connectStatus] || 'Unknown';
  const issues = connectStatusIssues(row);
  const tooltip = issues.length ? issues.join(', ') : 'All checks passing';
  return (
    <Tooltip title={tooltip}>
      <StatusBadge tone={connectStatusTone(row)} label={label} size="sm" dot />
    </Tooltip>
  );
};

const companyColumn = {
  title: 'Company',
  dataIndex: 'companyName',
  key: 'companyName',
  sorter: (a, b) => String(a.companyName || '').localeCompare(String(b.companyName || '')),
  render: (value) => (
    <span className="font-medium text-slate-900 dark:text-slate-100">{value}</span>
  ),
};

const modelColumn = {
  title: 'Model',
  dataIndex: 'model',
  key: 'model',
  width: 100,
  render: (value) => (
    <StatusBadge tone={MODEL_TONE[value] || 'slate'} label={MODEL_LABEL[value] || value || DASH} size="sm" />
  ),
};

const connectStatusColumn = {
  title: 'Connect status',
  dataIndex: 'connectStatus',
  key: 'connectStatus',
  width: 170,
  render: (_, row) => <ConnectStatusCell row={row} />,
};

const stripeFeeColumn = {
  title: 'Stripe fee',
  dataIndex: 'stripeFee',
  key: 'stripeFee',
  width: 190,
  align: 'right',
  sorter: compareNullableNumber('stripeFee'),
  render: (_, row) => <StripeFeeCell row={row} />,
};

const buildColumns = () => [
  companyColumn,
  modelColumn,
  connectStatusColumn,
  moneyColumn('Gross', 'grossAmount'),
  moneyColumn('Our net', 'ourShare'),
  moneyColumn('To restaurant', 'restaurantNet'),
  stripeFeeColumn,
  countColumn('Payments', 'paymentCount'),
  countColumn('Failed payments', 'failedCount'),
  moneyColumn('Fees waived', 'feesNotCollected'),
  moneyColumn('Average order', 'averageOrder'),
];

// What "Sadece sorunlular" (Only problems) treats as a problem row:
// chargesEnabled/payoutsEnabled false, a non-zero failed count, or a row the
// backend couldn't compute at all (`row.error`) — the last one matters most,
// since an uncomputable row is also the one dragging the totals footer's
// "Partial" stamp with it, and hiding it behind the filter meant to surface
// problems would leave that stamp with no visible cause on screen. Legacy
// rows without an error can never match on the first two — both flags and
// the failed count are null there, by design (no Connect account to
// restrict).
const hasIssue = (row) =>
  row.chargesEnabled === false ||
  row.payoutsEnabled === false ||
  (typeof row.failedCount === 'number' && row.failedCount > 0) ||
  !!row.error;

// The API now splits a company's money per-order rather than per-company:
// a company whose queried period spans its migration from legacy to
// Connect correctly comes back as two rows, same companyId, different
// model (PlatformFinanceService's per-order NOT EXISTS/ConnectPayment
// split — see final-fix-api-report.md). companyId alone is therefore no
// longer unique within `rows`; antd's Table needs a unique key per row or
// it silently collapses same-key rows and warns to the console, so the key
// is the pair, not just the id.
const rowKey = (row) => `${row.companyId ?? DASH}:${row.model ?? DASH}`;

const applyFilters = (rows, { model, onlyIssues, search }) => {
  const term = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (model !== 'all' && row.model !== model) return false;
    if (onlyIssues && !hasIssue(row)) return false;
    if (term && !String(row.companyName || '').toLowerCase().includes(term)) return false;
    return true;
  });
};

// Rule 3: the totals row is only trustworthy when `complete` is true — a
// `false` totals object still renders (it is not hidden either), but
// carries a visible "Partial" stamp instead of presenting a silently-short
// sum as final. Columns the API's totals DTO doesn't carry (Connect status,
// Stripe fee, Fees waived, Average order) stay blank here rather than
// guessing a sum platformApi.js's toPlatformTotals never returns.
//
// `totals` always covers every company the API returned for the period —
// it never re-computes off `filteredRows`, which is the client-side subset
// left after the model/only-issues/search filters run. When those filters
// have narrowed what's on screen, a plain "Total" would read as the sum of
// the visible rows and be wrong (worst case: "No records." above a
// non-zero total). `isFiltered` relabels it to "Total (all companies)"
// instead, so the number stays honest about what it actually sums.
const renderTotalsRow = (totals, isFiltered) => () => (
  <Table.Summary fixed>
    <Table.Summary.Row className="bg-slate-50/70 dark:bg-slate-800/80">
      <Table.Summary.Cell index={0}>
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
          {isFiltered ? 'Total (all companies)' : 'Total'}
          {totals.complete === false ? <StatusBadge tone="amber" label="Partial" size="sm" /> : null}
        </span>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={1} />
      <Table.Summary.Cell index={2} />
      <Table.Summary.Cell index={3} align="right">
        {money(totals.grossAmount)}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={4} align="right">
        {money(totals.ourShare)}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={5} align="right">
        {money(totals.restaurantNet)}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={6} />
      <Table.Summary.Cell index={7} align="right">
        {count(totals.paymentCount)}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={8} align="right">
        {count(totals.failedCount)}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={9} />
      <Table.Summary.Cell index={10} />
    </Table.Summary.Row>
  </Table.Summary>
);

// The company table: filters + columns + totals, all in one place because
// the filters exist to make this table's ~100 rows navigable, not as a
// feature of their own. platform-finance.js owns fetching (rows/totals/
// loading/error) and reads `from`/`to` back out of the same URL keys this
// component writes; model/onlyIssues/search never leave here at all.
const PlatformTable = ({ rows = [], totals = null, loading = false, error = '', onRetry }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const fromParam = searchParams.get(FROM_PARAM);
  const toParam = searchParams.get(TO_PARAM);
  const from = fromParam ? dayjs(fromParam) : defaultFrom();
  const to = toParam ? dayjs(toParam) : defaultTo();
  const model = searchParams.get(MODEL_PARAM) || 'all';
  const onlyIssues = searchParams.get(ISSUES_PARAM) === '1';
  const search = searchParams.get(SEARCH_PARAM) || '';

  // Same replace-mode idiom as connect-finance.js's changeTab: a filter
  // change is not a page the back button should walk back through.
  const updateParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    setSearchParams(next, { replace: true });
  };

  const handleRangeChange = (value) => {
    const [nextFrom, nextTo] = value || [];
    updateParams({
      [FROM_PARAM]: nextFrom ? nextFrom.format('YYYY-MM-DD') : null,
      [TO_PARAM]: nextTo ? nextTo.format('YYYY-MM-DD') : null,
    });
  };
  const handleModelChange = (value) => updateParams({ [MODEL_PARAM]: value && value !== 'all' ? value : null });
  const handleOnlyIssuesChange = (checked) => updateParams({ [ISSUES_PARAM]: checked ? '1' : null });
  const handleSearchChange = (event) => updateParams({ [SEARCH_PARAM]: event.target.value || null });

  const filteredRows = useMemo(
    () => applyFilters(rows, { model, onlyIssues, search }),
    [rows, model, onlyIssues, search],
  );

  // Narrower than the full set the totals actually sum — drives the
  // "Total (all companies)" relabel above (Fix: totals footer vs. filters).
  const isFiltered = filteredRows.length !== rows.length;

  const columns = useMemo(() => buildColumns(), []);

  return (
    <SectionCard
      title="Companies"
      description="Finance per company for the selected date range, legacy and Connect together."
      noBodyPadding
    >
      <div className="flex flex-wrap items-end gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-700">
        <TWDateRangePicker
          label="Date range"
          value={[from, to]}
          onChange={handleRangeChange}
          wrapperClassName="max-w-xs"
        />
        <TWSelect
          label="Model"
          options={MODEL_OPTIONS}
          value={model}
          onChange={handleModelChange}
          showSearch={false}
          allowClear={false}
          wrapperClassName="max-w-[160px]"
        />
        <TWInput
          label="Search company"
          leftIcon={MagnifyingGlassIcon}
          value={search}
          onChange={handleSearchChange}
          placeholder="Company name"
          wrapperClassName="max-w-xs"
        />
        <div className="flex items-center gap-2 pb-2">
          <ToggleSwitch checked={onlyIssues} onChange={handleOnlyIssuesChange} ariaLabel="Only problems" />
          <span className="text-sm text-slate-600 dark:text-slate-300">Only problems</span>
        </div>
      </div>

      {error ? (
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
          <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
          {onRetry ? <TWButton size="sm" label="Retry" onClick={onRetry} /> : null}
        </div>
      ) : loading ? (
        <div className="px-5" role="status" aria-label="Loading platform finance rows">
          <TableRowsSkeleton count={6} rowHeight="h-9" />
        </div>
      ) : (
        <TWDataTable
          columns={columns}
          data={filteredRows}
          rowKey={rowKey}
          // No navigation for a row the backend couldn't compute (`error`,
          // e.g. the synthetic "(unassigned)" aggregate for orders whose
          // store has no company) or for anything else that isn't a real
          // company id — neither has a /dashboard/companies detail page to
          // land on. Two rows can legitimately share a companyId now (a
          // migrated company's legacy + Connect halves) and both correctly
          // lead to the same company detail page — no change needed here.
          rowClickPath={(row) =>
            row.error || !row.companyId || row.companyId === '(unassigned)'
              ? null
              : `/dashboard/companies/${row.companyId}`
          }
          empty={<span className="text-sm text-slate-500 dark:text-slate-400">No records.</span>}
          className="tw-table-card-inset"
          summary={totals ? renderTotalsRow(totals, isFiltered) : undefined}
        />
      )}
    </SectionCard>
  );
};

export default PlatformTable;
